import { createSelector } from "@reduxjs/toolkit";
import { formatAvailablePlans } from "queries/pricing.utils";
import { RootState } from "reduxUtils/store";
import { PRODUCT_DISPLAY_NAMES } from "utils/constants";
import { findCurrentRateCard } from "utils/rateCardHelpers/findCurrentRateCard";

/**
 * Select the current rate card based on the current contract and current date
 */
export const selectCurrentRateCard = createSelector(
  (state: RootState) => state.currentProduct,
  (state: RootState) => state.rateCards.rateCards,
  (currentProduct, rateCards) => {
    if (rateCards.length === 0 || !currentProduct.normalisedName)
      return undefined;

    return findCurrentRateCard(
      rateCards,
      currentProduct.normalisedName,
      new Date(),
    );
  },
);

/**
 * Select the formatted rate cards
 */
export const selectFormattedAvailablePlans = createSelector(
  (state: RootState) => state.rateCards.rateCards,
  (rateCards) => {
    if (!rateCards) return undefined;

    return formatAvailablePlans(rateCards);
  },
);

/**
 * Select the current plan from the formatted available plans
 */
export const selectCurrentPlan = createSelector(
  selectFormattedAvailablePlans,
  (state: RootState) => state.currentProduct.id,
  (formattedAvailablePlans, currentProductId) => {
    if (!formattedAvailablePlans) return undefined;

    return formattedAvailablePlans.find(
      (availablePlans) => availablePlans.id === currentProductId,
    );
  },
);

/**
 * Select the other plans from the formatted available plans
 */
export const selectOtherPlans = createSelector(
  selectFormattedAvailablePlans,
  (state: RootState) => state.currentProduct.id,
  (formattedAvailablePlans, currentProductId) => {
    if (!formattedAvailablePlans) return [];

    return formattedAvailablePlans.filter(
      (availablePlan) =>
        availablePlan.id !== currentProductId &&
        availablePlan.productName.toLowerCase() !==
          PRODUCT_DISPLAY_NAMES.freestyle &&
        availablePlan.productName.toLowerCase() !==
          PRODUCT_DISPLAY_NAMES.business,
    );
  },
);

/**
 * see if User having any export channel (aka Solar User)
 * based on current rates
 */
export const isUserHavingExportChannel = createSelector(
  selectCurrentRateCard,
  (currentRateCard) => {
    if (!currentRateCard) return undefined;

    const exportChannel = currentRateCard.attributes.usage_rates.find((rate) =>
      rate.channels.some((channel) => channel.content_code === "EG"),
    );

    return Boolean(exportChannel);
  },
);
